import React from 'react'
import { Selector } from '@reduxjs/toolkit'
import { Translation } from 'locale'
import { EntityTypeEnum, Permission } from 'api'
import { intersection } from 'lodash-es'
import { GlobalState } from 'store'

export interface RouteConfig {
  url: string
  redirectTo?: string | string[]
  permissions: Permission[]
  caption: Translation
  content?: React.JSX.Element
  toolbar?: React.JSX.Element
  icon?: JSX.Element
  isDefault?: boolean
  childRoutes?: RouteConfig[]
  itemCountSelector?: Selector<GlobalState, number>
  activityHighlightSelector?: Selector<GlobalState, boolean>
  alertHighlightSelector?: Selector<GlobalState, boolean>
  warnHighlightSelector?: Selector<GlobalState, boolean>
  visibleSelector?: Selector<GlobalState, boolean>
  renderer?: (routeConfig: RouteConfig) => JSX.Element
  activeEntitySubscriptions: EntityTypeEnum[]
}

const urlParamsRegex = /\/:\w+\??/g

export const removeUrlParamPlaceholders = (url: string) => url.replace(urlParamsRegex, '')

const hasRoutePermission = (route: RouteConfig, permissions: Permission[]) =>
  route.permissions.length === 0 || intersection(permissions, route.permissions).length > 0

const getPermissionedRoutes = (routes: RouteConfig[], permissions: Permission[], base: string): RouteConfig[] =>
  routes
    .filter(route => hasRoutePermission(route, permissions))
    .map(({ url, redirectTo, childRoutes, ...rest }) => ({
      url: `${base}${url}`,
      redirectTo,
      childRoutes: childRoutes && getPermissionedRoutes(childRoutes, permissions, `${base}${removeUrlParamPlaceholders(url)}`),
      ...rest
    }))

export const buildPermissionedRoutes = (routes: RouteConfig[], permissions: Permission[]): RouteConfig[] => {
  const permissionedRoutes = getPermissionedRoutes(routes, permissions, '')

  // if no default route set the first top level route
  if (permissionedRoutes.length > 0 && permissionedRoutes.every(route => !route.isDefault)) {
    permissionedRoutes[0].isDefault = true
  }

  return permissionedRoutes
}

export const flattenRoutes = (routes: RouteConfig[]): RouteConfig[] =>
  routes.reduce(
    (routeConfigs, routeConfig) =>
      routeConfig.childRoutes ? [...routeConfigs, routeConfig, ...flattenRoutes(routeConfig.childRoutes)] : [...routeConfigs, routeConfig],
    []
  )
